import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

/**
 * Secure key-value store for small, long-lived secrets — the anonymous
 * player id (the key under which the server holds the player's wallet +
 * profile backup).
 *
 * <p>NATIVE (iOS / Android): the {@code capacitor-secure-storage-plugin}
 * Keychain / KeyStore. iOS Keychain items survive an app delete + reinstall
 * (we never clear them), so the player id — and thus the route back to the
 * server-side profile — survives a reinstall with NO login. That's what
 * makes no-login account recovery work.
 *
 * <p>WEB (dev server / PWA): plain localStorage. Survives relaunch but not a
 * cache wipe; kept deliberately raw (not the plugin's web shim) so the
 * {@code ?dev=fresh} tool's {@code removeItem('goodtime.playerId')} still
 * resets identity and web-dev storage stays inspectable.
 */
const isNative = Capacitor.isNativePlatform();

/** Read a stored secret, or null if absent / storage unavailable. */
export async function getSecure(key: string): Promise<string | null> {
  if (isNative) {
    try {
      const { value } = await SecureStoragePlugin.get({ key });
      return value;
    } catch {
      // The plugin REJECTS on a missing key (rather than returning null) —
      // treat that, and any Keychain error, as "no value".
      return null;
    }
  }
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Persist a secret. Silently degrades if storage is unavailable. */
export async function setSecure(key: string, value: string): Promise<void> {
  if (isNative) {
    try {
      await SecureStoragePlugin.set({ key, value });
    } catch {
      // Non-fatal — the id just won't persist from this write; a later write
      // (or next launch's mint) recovers.
    }
    return;
  }
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, value);
  } catch {
    // private mode / quota exceeded — degrade to in-session only.
  }
}
